import { useMemo, useCallback } from 'react';
import { useLocation } from 'react-router';
import { parse } from 'query-string';
import { useSelector } from 'react-redux';
import { QueryToCookieConverter } from 'components/Onboarding/shared/QueryToCookieConverter/QueryToCookieConverter';
import { formatPriceToNumber } from 'utils/formatPriceToNumber';
import { hashValueSha2WithoutSecret } from 'utils/hashValueSha2';
import segment, {
  ACCEPT_HIPAA_MARKETING_AUTHORIZATION,
  ACCOUNT_CREATE,
  CHOOSE_OPTION,
  FUNNEL_COMPLETE_EMAIL_INCLUDED,
  FUNNEL_COMPLETE_EMAIL_INCLUDED_MEDICATION_CASH,
  FUNNEL_COMPLETE_EMAIL_INCLUDED_MEDICATION_INI,
  FUNNEL_COMPLETE_EMAIL_INCLUDED_THERAPY_CASH,
  FUNNEL_COMPLETE_EMAIL_INCLUDED_THERAPY_INI,
  INITIATE_FUNNEL_COMPLETE,
  ITrackSubscriptionParams,
  ORDER_COMPLETED,
  PODSCRIBE_ACCOUNT_CREATE,
  PODSCRIBE_ORDER_COMPLETED,
  FUNNEL_COMPLETE_ADD,
  convertCampaignId,
  mapSubscriptionTypeByOfferingKey,
  FUNNEL_STARTED
} from 'utils/segment';
import { useKetchContext } from 'contexts/KetchContext';
import { Store } from 'types/store';
import { useAnalyticsPlatform } from './useAnalyticsPlatform';
import { BING_ADS, IAnalyticsData } from './useAnalyticsPlatform/types';
import useMountain from './useMountain';

export const ACCOUNT_CREATE_LTV = 25;
export const HIPAA_AUTH_ACCEPTED_LTV = 30;
export const CHOOSE_OPTION_LTV = 35;
export const FUNNEL_COMPLETE_ADD_LTV = 40;
export const INITIATE_FUNNEL_COMPLETE_LTV = 45;

export const standardIntegrations = {
  All: false,
  Heap: true,
  'TikTok Event Sharing (get-cerebral)': true,
  'Facebook Event Sharing (get-cerebral)': true,
  'Atwave Event Sharing (get-cerebral)': true,
  'Datadog Function US (get-cerebral)': true,
  'Google Ads Conversions': true,
  'Slack (Actions)': true,
  StackAdapt: true
};

export const useCoreMarketingSegmentEvents = () => {
  const location = useLocation();
  const { trackCustomEvent } = useAnalyticsPlatform();
  const { trackMountainPage, trackMountainConversion } = useMountain();
  const { purposes } = useKetchContext();
  const hipaaMarketingAuthorization = useSelector(
    (store: Store) => store.patient_reducer.hipaaAuthorizationStatus === 'accepted'
  );
  const zipcode = useSelector((store: Store) => store.global_reducer.current_user.attributes.postal_code);
  const email = useSelector((store: Store) => store.global_reducer.current_user.attributes.email);

  // Memoize cookie/query param values - these don't change during component lifecycle
  const gclid = useMemo(() => new QueryToCookieConverter('gclid').getCookieValue(), []);
  const fbclid = useMemo(() => new QueryToCookieConverter('fbclid').getCookieValue(), []);
  const ttclid = useMemo(() => new QueryToCookieConverter('ttclid').getCookieValue(), []);
  const awclid = useMemo(() => new QueryToCookieConverter('awclid').getCookieValue(), []);
  const saclid = useMemo(() => new QueryToCookieConverter('saclid').getCookieValue(), []);
  const irclid = useMemo(() => new QueryToCookieConverter('irclickid').getCookieValue(), []);

  const PLAN_EVENT_MAP: Record<string, string> = {
    A1: FUNNEL_COMPLETE_EMAIL_INCLUDED_MEDICATION_CASH,
    A2: FUNNEL_COMPLETE_EMAIL_INCLUDED_MEDICATION_INI,
    B1: FUNNEL_COMPLETE_EMAIL_INCLUDED_THERAPY_CASH,
    B2: FUNNEL_COMPLETE_EMAIL_INCLUDED_THERAPY_INI
  };

  const trackSignUp = useCallback(
    (opts?: { email: string }) => {
      /* When someone clicks an Impact Affiliate Link, and get to our homepage, we save the irclickid
       * into cookie, we then check if it exist on Account Creation and send it to segment
       * to correctly match the conversion in Impact
       */
      const referrer = irclid
        ? {
            type: 'impactRadius',
            id: irclid
          }
        : undefined;
      const { search } = location;
      const urlParams = parse(search);
      const appsflyerObj = segment.urlParamsToContext(urlParams);

      segment.track(
        ACCOUNT_CREATE,
        {
          gclid,
          fbclid,
          ttclid,
          awclid,
          ltv: ACCOUNT_CREATE_LTV,
          email: hashValueSha2WithoutSecret(opts?.email as string),
          isKetchAdvertisingAnalyticsAccepted: !!purposes.advertising_analytics
        },
        {
          ...appsflyerObj,
          integrations: {
            ...(appsflyerObj?.integrations || {}),
            ...standardIntegrations,
            Iterable: true,
            'Impact Partnership Cloud': true
          },
          referrer
        }
      );

      segment.track(
        PODSCRIBE_ACCOUNT_CREATE,
        { email: opts?.email },
        { integrations: { All: false, 'Podscribe (Actions)': true } }
      );

      trackCustomEvent({ platforms: [BING_ADS], eventName: ACCOUNT_CREATE, ltv: ACCOUNT_CREATE_LTV } as IAnalyticsData);
    },
    [gclid, fbclid, ttclid, awclid, irclid, location, purposes.advertising_analytics, trackCustomEvent]
  );

  const trackHipaaMarketingAuthorizationAccepted = useCallback(() => {
    segment.track(
      ACCEPT_HIPAA_MARKETING_AUTHORIZATION,
      {
        hipaa_marketing_authorization: true,
        ttclid,
        fbclid,
        gclid,
        zipcode,
        email,
        ltv: HIPAA_AUTH_ACCEPTED_LTV
      },
      {
        integrations: {
          ...standardIntegrations,
          Iterable: true
        }
      }
    );

    trackCustomEvent({
      platforms: [BING_ADS],
      eventName: FUNNEL_STARTED,
      ltv: HIPAA_AUTH_ACCEPTED_LTV
    } as IAnalyticsData);
    trackMountainPage({ eventName: FUNNEL_STARTED, ltv: HIPAA_AUTH_ACCEPTED_LTV });
  }, [ttclid, fbclid, gclid, zipcode, email, trackCustomEvent, trackMountainPage]);

  const trackSubscription = useCallback(
    ({
      offeringKey,
      price,
      campaignId,
      orderId,
      planId,
      patientId,
      isStrongStart,
      planNameAlias,
      ltv,
      subscriptionFlow
    }: ITrackSubscriptionParams): void => {
      let subscriptionType = mapSubscriptionTypeByOfferingKey[offeringKey] || 'Subscription Signup';

      if (isStrongStart) {
        subscriptionType = `Start Strong + ${subscriptionType}`;
      }

      const product = {
        id: subscriptionType,
        name: subscriptionType,
        quantity: 1,
        price: formatPriceToNumber(price, 85),
        patient_plan: `${patientId}-${planId}`,
        plan_name: offeringKey,
        order_id: orderId
      };

      const orderCompletedParams = {
        user: {
          email,
          userId: patientId
        },
        items: [product],
        email,
        type: subscriptionType,
        plan_id: planId,
        patient_plan: `${patientId}-${planId}`,
        plan_name: offeringKey,
        products: [product],
        campaignId: campaignId ? convertCampaignId(campaignId) : null,
        ketch_advertising_analytics: !!purposes.advertising_analytics,
        gclid,
        fbclid,
        ttclid,
        awclid,
        planNameAlias,
        ltv,
        subscriptionFlow,
        hipaa_marketing_authorization: hipaaMarketingAuthorization === true
      };

      segment.track(subscriptionType, orderCompletedParams, {
        integrations: {
          All: false,
          Iterable: true
        }
      });
      segment.track(ORDER_COMPLETED, orderCompletedParams, {
        integrations: standardIntegrations
      });
      segment.track(
        FUNNEL_COMPLETE_EMAIL_INCLUDED,
        {
          email,
          hipaa_marketing_authorization: hipaaMarketingAuthorization === true,
          ttclid,
          fbclid,
          gclid,
          saclid,
          zipcode,
          planNameAlias,
          ltv,
          subscriptionFlow
        },
        {
          integrations: standardIntegrations
        }
      );
      segment.track(
        PODSCRIBE_ORDER_COMPLETED,
        { total: ltv, email },
        { integrations: { All: false, 'Podscribe (Actions)': true } }
      );
      segment.track(
        ORDER_COMPLETED,
        {
          total: ltv,
          email: hashValueSha2WithoutSecret(email as string)
        },
        {
          integrations: { All: false, 'Impact Partnership Cloud': true },
          referrer: irclid ? { type: 'impactRadius', id: irclid } : undefined
        }
      );

      if (planNameAlias && PLAN_EVENT_MAP[planNameAlias]) {
        const eventName = PLAN_EVENT_MAP[planNameAlias];
        segment.track(
          eventName,
          {
            email,
            hipaa_marketing_authorization: hipaaMarketingAuthorization === true,
            ttclid,
            fbclid,
            gclid,
            saclid,
            zipcode,
            planNameAlias,
            ltv,
            subscriptionFlow
          },
          {
            integrations: standardIntegrations
          }
        );
      }

      trackCustomEvent({
        platforms: [BING_ADS],
        eventName: FUNNEL_COMPLETE_EMAIL_INCLUDED,
        planNameAlias,
        ltv
      } as IAnalyticsData);
      if (hipaaMarketingAuthorization) {
        const mountainOrderId = `${FUNNEL_COMPLETE_EMAIL_INCLUDED}-${hashValueSha2WithoutSecret(email as string)}`;
        trackMountainConversion({ eventName: mountainOrderId, ltv: ltv || 0 });
      }
    },
    [
      email,
      purposes.advertising_analytics,
      gclid,
      fbclid,
      ttclid,
      awclid,
      saclid,
      irclid,
      zipcode,
      hipaaMarketingAuthorization,
      trackCustomEvent,
      trackMountainConversion
    ]
  );

  const trackChooseOption = useCallback(
    ({ planNameAlias, subscriptionFlow }: { planNameAlias?: string; subscriptionFlow?: string }) => {
      segment.track(
        CHOOSE_OPTION,
        {
          hipaa_marketing_authorization: hipaaMarketingAuthorization,
          gclid,
          fbclid,
          ttclid,
          planNameAlias,
          zipcode: hipaaMarketingAuthorization ? zipcode : null,
          email: hipaaMarketingAuthorization ? email : null,
          ltv: CHOOSE_OPTION_LTV,
          subscriptionFlow
        },
        {
          integrations: standardIntegrations
        }
      );

      trackCustomEvent({
        platforms: [BING_ADS],
        eventName: CHOOSE_OPTION,
        ltv: CHOOSE_OPTION_LTV,
        planNameAlias
      } as IAnalyticsData);
      if (hipaaMarketingAuthorization) {
        trackMountainPage({ eventName: CHOOSE_OPTION, planNameAlias, ltv: CHOOSE_OPTION_LTV });
      }
    },
    [hipaaMarketingAuthorization, gclid, fbclid, ttclid, zipcode, email, trackCustomEvent, trackMountainPage]
  );

  const trackFunnelCompleteAdd = useCallback(
    ({ planNameAlias, subscriptionFlow }: { planNameAlias?: string; subscriptionFlow?: string }) => {
      segment.track(
        FUNNEL_COMPLETE_ADD,
        {
          hipaa_marketing_authorization: hipaaMarketingAuthorization,
          gclid,
          fbclid,
          ttclid,
          planNameAlias,
          zipcode: hipaaMarketingAuthorization ? zipcode : null,
          email: hipaaMarketingAuthorization ? email : null,
          ltv: FUNNEL_COMPLETE_ADD_LTV,
          subscriptionFlow
        },
        {
          integrations: standardIntegrations
        }
      );

      trackCustomEvent({
        platforms: [BING_ADS],
        eventName: FUNNEL_COMPLETE_ADD,
        ltv: FUNNEL_COMPLETE_ADD_LTV,
        planNameAlias
      } as IAnalyticsData);
      if (hipaaMarketingAuthorization) {
        trackMountainPage({ eventName: FUNNEL_COMPLETE_ADD, planNameAlias, ltv: FUNNEL_COMPLETE_ADD_LTV });
      }
    },
    [hipaaMarketingAuthorization, gclid, fbclid, ttclid, zipcode, email, trackCustomEvent, trackMountainPage]
  );

  const trackInitiateFunnelComplete = useCallback(
    ({ planNameAlias, subscriptionFlow }: { planNameAlias?: string; subscriptionFlow?: string }) => {
      segment.track(
        INITIATE_FUNNEL_COMPLETE,
        {
          hipaa_marketing_authorization: hipaaMarketingAuthorization,
          gclid,
          fbclid,
          ttclid,
          planNameAlias,
          zipcode: hipaaMarketingAuthorization ? zipcode : null,
          email: hipaaMarketingAuthorization ? email : null,
          ltv: INITIATE_FUNNEL_COMPLETE_LTV,
          subscriptionFlow
        },
        {
          integrations: standardIntegrations
        }
      );

      trackCustomEvent({
        platforms: [BING_ADS],
        eventName: INITIATE_FUNNEL_COMPLETE,
        ltv: INITIATE_FUNNEL_COMPLETE_LTV,
        planNameAlias
      } as IAnalyticsData);
      if (hipaaMarketingAuthorization) {
        trackMountainPage({ eventName: INITIATE_FUNNEL_COMPLETE, planNameAlias, ltv: INITIATE_FUNNEL_COMPLETE_LTV });
      }
    },
    [hipaaMarketingAuthorization, gclid, fbclid, ttclid, zipcode, email, trackCustomEvent, trackMountainPage]
  );

  return {
    trackSignUp,
    trackHipaaMarketingAuthorizationAccepted,
    trackSubscription,
    trackChooseOption,
    trackFunnelCompleteAdd,
    trackInitiateFunnelComplete
  };
};
